import type { DockviewApi } from 'dockview-core';
import { PANEL_TITLES, type PanelId } from '@trajectory/ui';

interface PanelPlacement {
  id: PanelId;
  position?: { referencePanel: PanelId; direction: 'left' | 'right' | 'above' | 'below' | 'within' };
  initialWidth?: number;
  initialHeight?: number;
}

/**
 * The out-of-the-box arrangement: mission/rocket/orbit editors tabbed on the
 * left, 3D viewport and 2D map tabbed centre-top with charts and the timeline
 * stacked below, and inspector/analytics/optimizer tabbed on the right — the
 * same three-column mission-control layout the plan's component hierarchy
 * describes. `layoutStore` reuses this list both to build the layout from
 * scratch and to know where a single re-opened panel belongs.
 */
const DEFAULT_PLACEMENTS: PanelPlacement[] = [
  { id: 'mission-explorer', initialWidth: 300 },
  { id: 'rocket-editor', position: { referencePanel: 'mission-explorer', direction: 'within' } },
  { id: 'orbit-editor', position: { referencePanel: 'mission-explorer', direction: 'within' } },
  { id: 'viewport-3d', position: { referencePanel: 'mission-explorer', direction: 'right' } },
  { id: 'map-2d', position: { referencePanel: 'viewport-3d', direction: 'within' } },
  { id: 'charts', position: { referencePanel: 'viewport-3d', direction: 'below' }, initialHeight: 280 },
  { id: 'timeline', position: { referencePanel: 'charts', direction: 'below' }, initialHeight: 90 },
  { id: 'inspector', position: { referencePanel: 'viewport-3d', direction: 'right' }, initialWidth: 320 },
  { id: 'analytics', position: { referencePanel: 'inspector', direction: 'within' } },
  { id: 'optimizer', position: { referencePanel: 'inspector', direction: 'within' } },
  { id: 'transfer-planner', position: { referencePanel: 'inspector', direction: 'within' } },
];

export function buildDefaultLayout(api: DockviewApi): void {
  api.clear();
  for (const placement of DEFAULT_PLACEMENTS) {
    api.addPanel({
      id: placement.id,
      component: placement.id,
      title: PANEL_TITLES[placement.id],
      position: placement.position,
      initialWidth: placement.initialWidth,
      initialHeight: placement.initialHeight,
      // A panel tabbed onto an existing one would otherwise steal that
      // group's active tab just by being added last; keeping it inactive
      // preserves the first-added panel in each group (Mission Explorer,
      // 3D Viewport, Inspector) as the one actually shown by default.
      inactive: placement.position?.direction === 'within',
    });
  }
}

/** Re-adds a single closed panel to its usual spot, or a fresh group if that spot is gone. */
export function addPanelToDefaultPosition(api: DockviewApi, id: PanelId): void {
  const placement = DEFAULT_PLACEMENTS.find((p) => p.id === id);
  if (!placement) return;

  const referenceStillOpen = placement.position ? Boolean(api.getPanel(placement.position.referencePanel)) : true;

  api.addPanel({
    id: placement.id,
    component: placement.id,
    title: PANEL_TITLES[placement.id],
    position: referenceStillOpen ? placement.position : undefined,
  });
}
